/**
 * Import Job Detail Modal
 *
 * Shows job summary, metrics, error breakdown, and per-record logs
 * with filtering and pagination.
 */
import { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  importsAPI,
  type ImportJobDetail,
  type ImportLog,
  type ImportSource,
} from '../../lib/api/imports';

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

interface StatusBadgeProps { status: string }
const STATUS_STYLES: Record<string, string> = {
  completed: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  failed: 'bg-red-50 border-red-200 text-red-700',
  partial: 'bg-amber-50 border-amber-200 text-amber-700',
  running: 'bg-blue-50 border-blue-200 text-blue-700',
  pending: 'bg-slate-50 border-slate-200 text-slate-700',
};

function StatusBadge({ status }: StatusBadgeProps) {
  const style = STATUS_STYLES[status] || STATUS_STYLES['pending'];
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${style}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

type LogFilterType = 'all' | 'failed' | 'created' | 'updated' | 'skipped';

interface JobDetailModalProps {
  jobId: string | null;
  /** Import source to fetch from: pna or atlascopco */
  source?: ImportSource;
  onClose: () => void;
}

export default function JobDetailModal({ jobId, source = 'pna', onClose }: JobDetailModalProps) {
  const [job, setJob] = useState<ImportJobDetail | null>(null);
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [logFilter, setLogFilter] = useState<LogFilterType>('all');
  const [logPage, setLogPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const [sampleStage, setSampleStage] = useState<'raw' | 'processed'>('raw');
  const [showSample, setShowSample] = useState(false);
  const [logTotalPages, setLogTotalPages] = useState(1);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setLogs([]);
      setLogFilter('all');
      setLogPage(1);
      return;
    }
    setLoading(true);
    Promise.all([
      importsAPI.getJob(jobId, source),
      importsAPI.getJobLogs(jobId, { page: 1 }, source),
    ]).then(([jobData, logsData]) => {
      setJob(jobData);
      setLogs(logsData.results);
      setLogTotalPages(Math.ceil(logsData.count / 50));
    }).catch((err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to load job details';
      toast.error(message);
    }).finally(() => setLoading(false));
  }, [jobId, source]);

  useEffect(() => {
    if (!jobId) return;
    const params: Record<string, string | number> = { page: logPage };
    if (logFilter !== 'all') params['action'] = logFilter;
    importsAPI.getJobLogs(jobId, params, source).then(data => {
      setLogs(data.results);
      setLogTotalPages(Math.ceil(data.count / 50));
    }).catch(() => { /* ignore */ });
  }, [jobId, logFilter, logPage, source]);

  return (
    <Dialog open={!!jobId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Job Detail</DialogTitle>
          <DialogDescription className="sr-only">Detailed view of import job and per-record logs</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : job ? (
          <div className="space-y-6">
            {/* Job Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <StatusBadge status={job.status} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Data Type</p>
                <p className="text-sm font-medium">{job.data_type_display}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="text-sm font-medium">{job.customer_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Duration</p>
                <p className="text-sm font-medium">{formatDuration(job.duration_seconds)}</p>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-5 gap-3">
              {[
                { label: 'Total', value: job.records_processed, color: '' },
                { label: 'Created', value: job.records_created, color: 'text-emerald-600' },
                { label: 'Updated', value: job.records_updated, color: 'text-blue-600' },
                { label: 'Skipped', value: job.records_skipped, color: 'text-amber-600' },
                { label: 'Failed', value: job.records_failed, color: 'text-red-600' },
              ].map(m => (
                <div key={m.label} className="text-center p-3 bg-muted/30 rounded-lg">
                  <p className={`text-xl font-bold tabular-nums ${m.color}`}>{m.value.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{m.label}</p>
                </div>
              ))}
            </div>

            {/* Error Message */}
            {job.error_message && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{job.error_message}</p>
              </div>
            )}

            {/* Error Breakdown */}
            {job.error_breakdown && job.error_breakdown.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Error Breakdown</p>
                <div className="space-y-1">
                  {job.error_breakdown.map(e => (
                    <div key={e.error_code} className="flex items-center justify-between py-1.5 px-3 bg-red-50 rounded text-sm">
                      <span className="font-mono text-red-800">{e.error_code || 'unknown'}</span>
                      <Badge variant="destructive" className="text-xs">{e.count}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Debug Sample: first 10 records captured at import time */}
            {((job.debug_sample_raw?.length ?? 0) > 0 || (job.debug_sample_processed?.length ?? 0) > 0) && (
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowSample(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-muted/50"
                >
                  <span>Debug Sample (first 10 records)</span>
                  <span className="text-xs text-muted-foreground">
                    {showSample ? 'hide' : 'show'}
                  </span>
                </button>
                {showSample && (
                  <div className="border-t p-3 space-y-2">
                    <div className="flex gap-1">
                      {(['raw', 'processed'] as const).map(s => (
                        <button
                          key={s}
                          onClick={() => setSampleStage(s)}
                          className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                            sampleStage === s ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          }`}
                        >
                          {s === 'raw' ? 'Raw (from source)' : 'Processed (normalized)'}
                        </button>
                      ))}
                    </div>
                    <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-slate-50 p-2 rounded max-h-96 overflow-auto">
                      {JSON.stringify(
                        sampleStage === 'raw' ? job.debug_sample_raw : job.debug_sample_processed,
                        null,
                        2,
                      )}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Record Logs */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">Record Logs</p>
                <div className="flex gap-1">
                  {(['all', 'failed', 'created', 'updated', 'skipped'] as const).map(f => (
                    <button
                      key={f}
                      onClick={() => { setLogFilter(f); setLogPage(1); }}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        logFilter === f ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No logs for this filter</p>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="text-left p-2 font-medium">Action</th>
                        <th className="text-left p-2 font-medium">Entity</th>
                        <th className="text-left p-2 font-medium">Reference</th>
                        <th className="text-left p-2 font-medium">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map(log => {
                        const rawRecord = (log.fields_changed as { raw_record?: Record<string, unknown> } | undefined)?.raw_record;
                        const expanded = expandedLogId === log.id;
                        return (
                          <>
                            <tr key={log.id} className="border-b last:border-0">
                              <td className="p-2">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  log.action === 'failed' ? 'bg-red-100 text-red-800' :
                                  log.action === 'created' ? 'bg-emerald-100 text-emerald-800' :
                                  log.action === 'updated' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>{log.action_display || log.action}</span>
                              </td>
                              <td className="p-2 text-muted-foreground">{log.entity_type}</td>
                              <td className="p-2 font-mono text-xs">{log.entity_reference || log.source_key || '-'}</td>
                              <td className={`p-2 text-xs max-w-[300px] truncate ${log.error_message ? 'text-red-600' : 'text-muted-foreground'}`} title={log.error_message}>
                                {log.error_message || '-'}
                                {rawRecord && (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedLogId(expanded ? null : log.id)}
                                    className="ml-2 underline text-blue-600"
                                  >
                                    {expanded ? 'hide payload' : 'view payload'}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {expanded && rawRecord && (
                              <tr key={`${log.id}-raw`} className="border-b last:border-0 bg-slate-50">
                                <td colSpan={4} className="p-2">
                                  <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                                    {JSON.stringify(rawRecord, null, 2)}
                                  </pre>
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Log Pagination */}
              {logTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-3">
                  <Button size="sm" variant="outline" disabled={logPage <= 1} onClick={() => setLogPage(p => p - 1)}>Prev</Button>
                  <span className="text-xs text-muted-foreground">Page {logPage} of {logTotalPages}</span>
                  <Button size="sm" variant="outline" disabled={logPage >= logTotalPages} onClick={() => setLogPage(p => p + 1)}>Next</Button>
                </div>
              )}
            </div>

            {/* Job Metadata */}
            <div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
              <p>Job ID: <span className="font-mono">{job.job_id}</span></p>
              <p>Source: {job.source_display} {job.source_file_name && `(${job.source_file_name})`}</p>
              <p>Created: {new Date(job.created_at).toLocaleString()}</p>
              {job.celery_task_id && <p>Celery Task: <span className="font-mono">{job.celery_task_id}</span></p>}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
